// FloatingChatbot.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { baseUrl } from "../Univ.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faTrash,
  faMinus,
  faPaperPlane,
  faComments,
} from "@fortawesome/free-solid-svg-icons";

const greetings = ["hi", "hello", "hey", "good morning", "good afternoon"];

const Chatbot = ({ onClose, onMinimize, onTyping }) => {
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("chatHistory");
    return saved
      ? JSON.parse(saved)
      : [
          {
            sender: "bot",
            text: "👋 Hi! I'm your hospital assistant. Ask me about doctors, medicines, or patients.",
          },
        ];
  });

  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("chatHistory", JSON.stringify(messages));
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMessage = input.trim();
    const newMessages = [...messages, { sender: "user", text: userMessage }];
    setMessages(newMessages);

    setIsTyping(true);
    onTyping(true); // notify parent (minimized bar)

    try {
      if (greetings.some((g) => userMessage.toLowerCase().includes(g))) {
        await new Promise((r) => setTimeout(r, 500)); // small delay for typing effect
        setMessages([
          ...newMessages,
          {
            sender: "bot",
            text: "👋 Hello! How can I assist you today? You can ask about doctors, medicines, or patients.",
          },
        ]);
      } else {
        const res = await axios.post(`${baseUrl}chatbot/`, { message: userMessage });
        await new Promise((r) => setTimeout(r, 500)); // delay for typing
        setMessages([...newMessages, { sender: "bot", text: res.data.reply }]);
      }
    } catch (err) {
      setMessages([...newMessages, { sender: "bot", text: "⚠️ Server not reachable." }]);
    }

    setInput("");
    setIsTyping(false);
    onTyping(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    localStorage.removeItem("chatHistory");
    setMessages([{ sender: "bot", text: "👋 Chat cleared. How can I help you?" }]);
  };

  return (
    <div
      style={{
        width: "400px",
        height: "500px",
        border: "1px solid #ccc",
        borderRadius: "10px",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#4CAF50",
          color: "#fff",
          padding: "10px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
        }}
      >
        <strong>💬 Hospital Assistant</strong>
        <div style={{ display: "flex", gap: "10px" }}>
          <FontAwesomeIcon
            icon={faMinus}
            title="Minimize"
            onClick={onMinimize}
            style={{ cursor: "pointer" }}
          />
          <FontAwesomeIcon
            icon={faTrash}
            title="Clear chat"
            onClick={handleClear}
            style={{ cursor: "pointer" }}
          />
          <FontAwesomeIcon
            icon={faTimes}
            title="Close"
            onClick={onClose}
            style={{ cursor: "pointer" }}
          />
        </div>
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "10px",
          background: "#f9f9f9",
        }}
      >
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              textAlign: msg.sender === "bot" ? "left" : "right",
              margin: "8px 0",
            }}
          >
            <span
              style={{
                background: msg.sender === "bot" ? "#e0e0e0" : "#4CAF50",
                color: msg.sender === "bot" ? "#000" : "#fff",
                padding: "8px 12px",
                borderRadius: "12px",
                display: "inline-block",
                maxWidth: "80%",
                whiteSpace: "pre-wrap",
              }}
            >
              {msg.text}
            </span>
          </div>
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <div style={{ textAlign: "left", margin: "8px 0" }}>
            <span
              style={{
                background: "#e0e0e0",
                color: "#000",
                padding: "8px 12px",
                borderRadius: "12px",
                display: "inline-block",
                maxWidth: "30px",
              }}
            >
              ...
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ display: "flex", padding: "10px", borderTop: "1px solid #ccc" }}>
        <input
          style={{
            flex: 1,
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            outline: "none",
          }}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Ask about doctor, patient, or medicine..."
        />
        <button
          onClick={handleSend}
          style={{
            padding: "10px 15px",
            marginLeft: "5px",
            background: "#4CAF50",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          <FontAwesomeIcon icon={faPaperPlane} />
        </button>
      </div>
    </div>
  );
};

const FloatingChatbot = () => {
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const handleOpen = () => {
    setVisible(true);
    setMinimized(false);
  };
  const handleClose = () => setVisible(false);
  const handleMinimize = () => setMinimized(true);

  return (
    <>
      {/* Chat window */}
      {visible && !minimized && (
        <div
          style={{
            position: "fixed",
            bottom: "90px",
            right: "30px",
            zIndex: 9999,
          }}
        >
          <Chatbot
            onClose={handleClose}
            onMinimize={handleMinimize}
            onTyping={setIsTyping}
          />
        </div>
      )}

      {/* Minimized bar */}
      {visible && minimized && (
        <div
          onClick={() => setMinimized(false)}
          style={{
            position: "fixed",
            bottom: "90px",
            right: "30px",
            zIndex: 9999,
            width: "200px",
            height: "50px",
            background: "#4CAF50",
            color: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "25px",
            cursor: "pointer",
            boxShadow: "0 0 8px rgba(0,0,0,0.3)",
            fontWeight: "bold",
          }}
        >
          <FontAwesomeIcon icon={faComments} style={{ marginRight: "10px" }} />
          {isTyping ? "Typing..." : "Chat (Minimized)"}
        </div>
      )}

      {/* Floating button */}
      {!visible && (
        <div
          onClick={handleOpen}
          style={{
            position: "fixed",
            bottom: "30px",
            right: "30px",
            width: "60px",
            height: "60px",
            backgroundColor: "#4CAF50",
            borderRadius: "50%",
            boxShadow: "0 0 8px rgba(0,0,0,0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            zIndex: 10000,
          }}
        >
          <FontAwesomeIcon icon={faComments} style={{ fontSize: "30px", color: "#fff" }} />
        </div>
      )}
    </>
  );
};

export default FloatingChatbot;
